import { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import PageShell from "@/components/PageShell";
import { EmptyState } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import {
  CloudArrowUp,
  DownloadSimple,
  Archive,
  Warning,
  Database,
  CheckCircle,
  ArrowClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function humanBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit"
    });
  } catch {
    return iso;
  }
}

export default function BackupsPage() {
  const [state, setState] = useState({ items: [], total: 0, retention_days: 90, configured: true });
  const [loading, setLoading] = useState(true);
  const [snapshotting, setSnapshotting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backups/list");
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const snapshotNow = async () => {
    if (!confirm("¿Generar un respaldo manual ahora?\n\nSe subirá una copia completa (clientes + facturas + planes + papelera) a Emergent Object Storage.")) return;
    setSnapshotting(true);
    try {
      const { data } = await api.post("/backups/snapshot-now");
      const c = data.counts || {};
      toast.success(
        `Respaldo generado · ${c.clients ?? 0} clientes · ${c.invoices ?? 0} facturas · ${c.plans ?? 0} planes`
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSnapshotting(false);
    }
  };

  const download = (id, format = "json") => {
    const token = localStorage.getItem("token");
    // Direct-download via anchor with auth query hack (backend accepts bearer or query token in this case)
    // For clean auth, open in a fetch and turn into blob:
    fetch(`${API}/backups/${id}/download?format=${format}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const cd = res.headers.get("content-disposition") || "";
        const match = /filename="([^"]+)"/.exec(cd);
        const fname = match ? match[1] : `backup-${id}.${format}`;
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.remove();
        }, 100);
      })
      .catch((e) => toast.error(`Descarga falló: ${e.message}`));
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar este respaldo? El archivo permanecerá en el storage pero no aparecerá en la lista.")) return;
    try {
      await api.delete(`/backups/${id}`);
      toast.success("Respaldo eliminado de la lista");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <PageShell
      section="Administración"
      page="Respaldos en la nube"
      context={`${state.total} snapshots · retención ${state.retention_days}d`}
      testid="backups-toolbar"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="backups-refresh"
            className="inline-flex items-center gap-1.5 px-3 h-9 text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-400 hover:text-slate-900 dark:hover:text-white rounded transition-colors"
            title="Recargar lista"
          >
            <ArrowClockwise size={14} />
          </button>
          <Button
            onClick={snapshotNow}
            disabled={snapshotting || !state.configured}
            data-testid="backups-snapshot-now"
            className="bg-slate-900 hover:bg-slate-800 text-white h-9"
          >
            <CloudArrowUp size={15} className="mr-1.5" />
            {snapshotting ? "Generando…" : "Generar respaldo"}
          </Button>
        </div>
      }
    >
      {!state.configured && (
        <div className="mb-5 border border-amber-200 bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30 rounded-md px-4 py-3 flex items-start gap-3">
          <Warning size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-[13px] text-amber-800 dark:text-amber-300 leading-relaxed">
            <strong>Emergent Object Storage no está configurado.</strong> Los respaldos automáticos están deshabilitados.
            Contacta al administrador para habilitar el almacenamiento en la nube.
          </div>
        </div>
      )}

      {/* Info strip */}
      <div className="mb-5 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-200 dark:divide-slate-800">
          <div className="px-5 py-4 relative">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-slate-900 dark:bg-slate-100" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold flex items-center gap-1.5">
              <Archive size={11} weight="fill" /> Snapshots almacenados
            </div>
            <div className="mt-1.5 text-2xl font-semibold font-mono tabular-nums text-slate-900 dark:text-slate-100">{state.total}</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">copias completas de la BD</div>
          </div>
          <div className="px-5 py-4 relative">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-blue-500" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">Retención</div>
            <div className="mt-1.5 text-2xl font-semibold font-mono tabular-nums text-slate-900 dark:text-slate-100">{state.retention_days}d</div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">purga automática tras vencer</div>
          </div>
          <div className="px-5 py-4 relative">
            <div className="absolute top-0 left-0 right-0 h-[2px] bg-emerald-500" />
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold flex items-center gap-1.5">
              <CheckCircle size={11} weight="fill" /> Estado
            </div>
            <div className="mt-1.5 text-lg font-semibold text-slate-900 dark:text-slate-100">
              {state.configured ? "Operativo" : "Inactivo"}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              {state.configured ? "Emergent Object Storage" : "requiere configuración"}
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Cargando…</div>
        ) : state.items.length === 0 ? (
          <EmptyState
            icon={Database}
            title="Sin respaldos aún"
            message="Genera el primer respaldo manualmente o espera al próximo snapshot automático (diario)."
            action={
              state.configured ? (
                <Button
                  onClick={snapshotNow}
                  disabled={snapshotting}
                  className="bg-slate-900 hover:bg-slate-800 text-white h-9"
                >
                  <CloudArrowUp size={15} className="mr-1.5" />
                  Generar primer respaldo
                </Button>
              ) : null
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="backups-table">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                  <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Fecha</th>
                  <th className="text-left px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Actor</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Clientes</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Facturas</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Planes</th>
                  <th className="text-right px-3 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Tamaño</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Descargar</th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((b) => (
                  <tr key={b.id} data-testid={`backup-row-${b.id}`} className="border-b border-slate-100 dark:border-slate-900 hover:bg-slate-50/50 dark:hover:bg-slate-900/40">
                    <td className="px-4 py-3 align-middle">
                      <div className="text-[13px] font-medium text-slate-900 dark:text-slate-100">
                        {formatDate(b.created_at)}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono mt-0.5 truncate max-w-[280px]" title={b.id}>
                        {b.id}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-middle text-[12px] text-slate-600 dark:text-slate-400">
                      {b.actor === "scheduler" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-600 dark:text-slate-400">
                          Automático
                        </span>
                      ) : (
                        <span className="text-[12px]">{b.actor}</span>
                      )}
                    </td>
                    <td className="px-3 py-3 align-middle text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
                      {b.counts?.clients ?? 0}
                    </td>
                    <td className="px-3 py-3 align-middle text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
                      {b.counts?.invoices ?? 0}
                    </td>
                    <td className="px-3 py-3 align-middle text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
                      {b.counts?.plans ?? 0}
                    </td>
                    <td className="px-3 py-3 align-middle text-right font-mono tabular-nums text-slate-600 dark:text-slate-400 text-[12px]">
                      {humanBytes(b.size_bytes)}
                    </td>
                    <td className="px-4 py-3 align-middle text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => download(b.id, "json")}
                          data-testid={`backup-download-json-${b.id}`}
                          className="inline-flex items-center gap-1 px-2 h-7 text-[11px] font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                          title="Descargar JSON completo"
                        >
                          <DownloadSimple size={12} /> JSON
                        </button>
                        <button
                          onClick={() => download(b.id, "csv")}
                          data-testid={`backup-download-csv-${b.id}`}
                          className="inline-flex items-center gap-1 px-2 h-7 text-[11px] font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded transition-colors"
                          title="Descargar clientes en CSV"
                        >
                          <DownloadSimple size={12} /> CSV
                        </button>
                        <button
                          onClick={() => remove(b.id)}
                          data-testid={`backup-delete-${b.id}`}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 rounded transition-colors"
                          title="Eliminar de la lista"
                        >
                          <Archive size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PageShell>
  );
}
